import { slicedToArray as _slicedToArray, defineProperty as _defineProperty } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useEffect } from 'react';
import { Button } from '@deal/components';
import { usePathContext } from '../../../../context/PathContext.js';
import classNames from 'classnames';
import retirevePreviousNode from '../../../../helpers/retrievePreviousNode.js';
import PlainText from '../../../../components/PlainText/index.js';
import useHandleNextNode from '../../../../hooks/useHandleNextNode.js';
import NodeErrorHeader from '../NodeErrorHeader/index.js';
import QuestionDisplayComponents from '../QuestionDisplayComponents/index.js';
import useSubmitQuestionsCommand from '../../../../commands/useSubmitQuestionsCommand.js';
import isSubmitButtonHidden from '../../../../helpers/isSubmitButtonHidden.js';
import constructSubmitButtonText from '../../../../helpers/constructSubmitButtonText.js';
import canNodeBeSubmitted from '../../../../helpers/canNodeBeSubmitted.js';
import doesNodeAutomaticallySubmit from '../../../../helpers/doesNodeAutomaticallySubmit.js';
import Question$1 from '../../../../components/Question/index.js';
import QuestionHeader from '../QuestionHeader/index.js';
import InputLabel from '../InputLabel/index.js';
import styles from './styles.css.js';

var Question = function Question() {
  var _a, _b, _c;

  var pathContext = usePathContext();
  var handleNextNode = useHandleNextNode();
  var previousNode = retirevePreviousNode(pathContext.pathGraphFlow, (_a = pathContext.activeNode) === null || _a === void 0 ? void 0 : _a.id);
  var questionViewModel = (_b = pathContext.activeNode) === null || _b === void 0 ? void 0 : _b.viewModel;

  var _useState = useState({}),
      _useState2 = _slicedToArray(_useState, 2),
      questionCommands = _useState2[0],
      updateQuestionCommands = _useState2[1];

  var _useState3 = useState({}),
      _useState4 = _slicedToArray(_useState3, 2),
      questionValiditySet = _useState4[0],
      updateQuestionValiditySet = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      canNodeSubmit = _useState6[0],
      setCanNodeSubmit = _useState6[1];
  /**
   * These are obfuscated to hooks to help the reusability of the logic if and
   * when we start to introduce new layouts.
   */


  var _useSubmitQuestionsCo = useSubmitQuestionsCommand(function (updatedPathGraphFlow) {
    handleNextNode({
      pathGraphFlow: updatedPathGraphFlow,
      onProgressToNextNode: function onProgressToNextNode() {
        // On complete of submitting, wipe the local state of commands
        updateQuestionCommands({});
        updateQuestionValiditySet({});
      }
    });
  }),
      _useSubmitQuestionsCo2 = _slicedToArray(_useSubmitQuestionsCo, 2),
      onQuestionsSubmitted = _useSubmitQuestionsCo2[0],
      loading = _useSubmitQuestionsCo2[1].loading;
  /**
   * There are certain criteria where the submit button would be hidden, in some cases we directly
   * submit the question from the answer. E.g. a list question that is single select
   *
   * If the path is completed and we're moving backwards through the path, submit is also disabled
   */


  var submitButtonHidden = isSubmitButtonHidden(questionViewModel) || ((_c = pathContext.pathGraphFlow) === null || _c === void 0 ? void 0 : _c.completed);
  useEffect(function () {
    var shouldNodeAutoSubmit = doesNodeAutomaticallySubmit(questionCommands, questionViewModel);
    var canNodeSubmit = canNodeBeSubmitted(questionValiditySet, questionCommands, questionViewModel);

    if (shouldNodeAutoSubmit && !loading && canNodeSubmit) {
      onQuestionsSubmitted(questionCommands);
    }

    setCanNodeSubmit(canNodeSubmit);
  }, [questionCommands, questionValiditySet]);
  var submitButtonText = constructSubmitButtonText(questionViewModel, questionCommands);
  return React.createElement("form", {
    className: classNames(styles.form, _defineProperty({}, styles.placementDefault, pathContext.pathPlacement === 'default')),
    onSubmit: function onSubmit(e) {
      e.preventDefault();
      /**
       * Not only check if the node can be submitted, but only allow this if there
       * is only one question on the page.
       */

      var canNodeSubmitViaForm = Object.values(questionCommands).length === 1 && canNodeBeSubmitted(questionValiditySet, questionCommands, questionViewModel);

      if (canNodeSubmitViaForm) {
        onQuestionsSubmitted(questionCommands);
      }
    }
  }, React.createElement(NodeErrorHeader, null), React.createElement("div", {
    className: classNames(styles.questionContainer, _defineProperty({}, styles.placementDefault, pathContext.pathPlacement === 'default'))
  }, questionViewModel.questions.map(function (question) {
    return React.createElement(React.Fragment, {
      key: question.id
    }, React.createElement(QuestionHeader, {
      question: question
    }), React.createElement(InputLabel, {
      question: question
    }), React.createElement(Question$1, {
      loading: loading,
      question: question,
      onQuestionValidityChanged: function onQuestionValidityChanged(isQuestionValid) {
        updateQuestionValiditySet(function (prevState) {
          return Object.assign(Object.assign({}, prevState), _defineProperty({}, question.id, {
            hasValidAnswer: isQuestionValid
          }));
        });
      },
      onQuestionUpdated: function onQuestionUpdated(updatedQuestionCommand) {
        updateQuestionCommands(function (prevState) {
          return Object.assign(Object.assign({}, prevState), _defineProperty({}, question.id, updatedQuestionCommand));
        });
      }
    }));
  })), React.createElement("div", {
    className: styles.questionDisplayComponents
  }, React.createElement(QuestionDisplayComponents, {
    displayComponents: questionViewModel.questionsDisplayComponents
  })), React.createElement("div", {
    className: classNames(styles.actions, _defineProperty({}, styles.placementModal, pathContext.pathPlacement === 'modal'))
  }, previousNode && React.createElement("div", {
    className: styles.backButton
  }, React.createElement(Button, {
    "data-testid": "path-back-button",
    disabled: loading,
    type: "button",
    size: "large",
    variant: "default-ghost",
    tabIndex: 2,
    onClick: function onClick() {
      updateQuestionCommands({});
      updateQuestionValiditySet({});
      pathContext.onSetActiveNode(previousNode);
    }
  }, "Back")), !submitButtonHidden && React.createElement("div", {
    className: styles.submitButton
  }, React.createElement(Button, {
    "data-testid": "path-submit-button",
    disabled: loading || !canNodeSubmit,
    type: "button",
    size: "large",
    tabIndex: 1,
    onClick: function onClick() {
      return onQuestionsSubmitted(questionCommands);
    }
  }, React.createElement(PlainText, {
    plainTextEntry: submitButtonText
  })))));
};

export default Question;
//# sourceMappingURL=index.js.map
